import type { DqStatRecord } from "@/features/adp/services/source/source.types";
import type { DataProfilingFieldEntry } from "@/features/adp/state/overlaySlice";

export interface ProfilingRow extends DataProfilingFieldEntry {
    stats: DqStatRecord | null;
    distinctCount: number;
    duplicate: number;
    nullValue: number;
}

export interface FieldStatsView {
    fieldName: string;
    fieldDataType: string;
    createdTimestamp: string | null;
    distinctCount: number;
    duplicate: number;
    nullValue: number;
    distinctPercent: number;
    duplicatePercent: number;
    nullPercent: number;
    totalRows: number;
    minValue: number | string | null;
    maxValue: number | string | null;
    mean: number | null;
    standardDeviation: number | null;
    median: number | null;
    isNumeric: boolean;
}

const NUMERIC_TYPES = new Set([
    "int",
    "integer",
    "bigint",
    "smallint",
    "tinyint",
    "long",
    "short",
    "byte",
    "float",
    "double",
    "decimal",
    "numeric",
    "real",
    "number",
]);

function indexStatsByFieldId(stats: DqStatRecord[]): Map<number, DqStatRecord> {
    const out = new Map<number, DqStatRecord>();
    for (const stat of stats) {
        out.set(stat.source_file_field_id, stat);
    }
    return out;
}

export function buildProfilingRows(
    fields: DataProfilingFieldEntry[],
    stats: DqStatRecord[] | undefined,
): ProfilingRow[] {
    const byId = indexStatsByFieldId(stats ?? []);
    return fields.map((field) => {
        const stat = byId.get(field.source_file_field_id) ?? null;
        return {
            ...field,
            stats: stat,
            distinctCount: stat?.distinct_count ?? 0,
            duplicate: stat?.duplicate ?? 0,
            nullValue: stat?.null_value ?? 0,
        };
    });
}

function clampPercent(value: number): number {
    if (!Number.isFinite(value)) return 0;
    if (value < 0) return 0;
    if (value > 100) return 100;
    return value;
}

export function buildFieldStatsView(row: ProfilingRow): FieldStatsView {
    const stats = row.stats;
    const distinctPercent = clampPercent(Number(stats?.distinct_percent ?? 0));
    const nullPercent = clampPercent(Number(stats?.null_percent ?? 0));
    const duplicatePercent = clampPercent(100 - distinctPercent - nullPercent);
    const totalCount = Number(stats?.total_count ?? 0);
    const nullValue = Number(stats?.null_value ?? 0);
    const isNumeric = NUMERIC_TYPES.has(row.field_data_type.trim().toLowerCase());

    return {
        fieldName: row.field_name,
        fieldDataType: row.field_data_type,
        createdTimestamp: stats?.created_datetime_timestamp ?? null,
        distinctCount: row.distinctCount,
        duplicate: row.duplicate,
        nullValue: row.nullValue,
        distinctPercent,
        duplicatePercent,
        nullPercent,
        totalRows: totalCount + nullValue,
        minValue: stats?.min_value ?? null,
        maxValue: stats?.max_value ?? null,
        mean: stats?.mean ?? null,
        standardDeviation: stats?.standard_deviation ?? null,
        median: stats?.median ?? null,
        isNumeric,
    };
}

export function formatProfilingTimestamp(iso: string | null): string {
    if (!iso) return "";
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    const dd = String(parsed.getDate()).padStart(2, "0");
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const yyyy = parsed.getFullYear();
    const hh = String(parsed.getHours()).padStart(2, "0");
    const mins = String(parsed.getMinutes()).padStart(2, "0");
    return `At ${dd}-${mm}-${yyyy}, ${hh}:${mins}`;
}

export function formatNumber(value: number | null | undefined, fractionDigits = 2): string {
    if (value == null || !Number.isFinite(Number(value))) return "—";
    const num = Number(value);
    if (Number.isInteger(num) && fractionDigits === 0) return String(num);
    return num.toLocaleString(undefined, {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: 0,
    });
}

export function formatScalar(value: number | string | null | undefined): string {
    if (value == null || value === "") return "—";
    if (typeof value === "number") {
        if (!Number.isFinite(value)) return "—";
        return Number.isInteger(value) ? String(value) : value.toFixed(2);
    }
    return value;
}
